import {Component, Inject, OnInit} from '@angular/core';
import {BaseCustomDialog} from '../base-custom-dialog';
import {ActionIconHelper} from '../../../../helper/action-icon.helper';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ACTION_DATA_SERVICE, ActionDataService} from '../../../../services/action/data.service';
import {LEADS_DATA_SERVICE, LeadsDataService} from '../../../../services/leads/leads-data.service';
import {
  IContact,
  ILeadData, LeadDataObject,
  SolveDuplicateContact
} from '../../../../models/leads/leads.model';
import { Router } from '@angular/router';
import {NewEmailComponent} from '../new-email/new-email.component';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {PhoneNumberHelper} from '../../../../helper/phone-number.helper';
import {HideLoader, ShowLoader} from '../../../store/loader/loader.actions';
import {Store} from '@ngxs/store';
import { ValidatorHelper } from '../../../../helper/validator.helper';

@Component({
  selector: 'fi-duplicate-check',
  templateUrl: './duplicate-check.component.html',
  styleUrls: ['./duplicate-check.component.scss'],
  standalone: false,
})
export class DuplicateCheckComponent extends BaseCustomDialog {

  contactForm = this.fb.group({
    Id: this.fb.control(null),
    ContactTypeId: this.fb.control(3),
    ContactMethodId: this.fb.control(1),
    Salutation: this.fb.control('', Validators.required),
    FirstName: this.fb.control('', [Validators.required, Validators.pattern(ValidatorHelper.nameValidator)]),
    LastName: this.fb.control('', [Validators.required, Validators.pattern(ValidatorHelper.nameValidator)]),
    CountryCode: this.fb.control(''),
    ZipCode: this.fb.control('', Validators.pattern(ValidatorHelper.zipValidator)),
    Address: this.fb.control('', Validators.pattern(ValidatorHelper.addressValidator)),
    AddressExt: this.fb.control('', Validators.pattern(ValidatorHelper.addressValidator)),
    City: this.fb.control('', Validators.pattern(ValidatorHelper.cityValidator)),
    Email: this.fb.control('', [Validators.email, Validators.pattern(ValidatorHelper.emailValidator)]),
    AdditionalEmail: this.fb.control('', [Validators.email, Validators.pattern(ValidatorHelper.emailValidator)]),
    Phone: this.fb.control('', [Validators.pattern(PhoneNumberHelper.phoneValidator)]),
    PhonePrefix: this.fb.control('', [Validators.pattern(PhoneNumberHelper.countryCodeValidator)]),
    Mobile: this.fb.control('', [Validators.pattern(PhoneNumberHelper.phoneValidator)]),
    MobilePrefix: this.fb.control('', [Validators.pattern(PhoneNumberHelper.countryCodeValidator)]),
    ProvinceCode: this.fb.control(''),
    Website: this.fb.control('', Validators.pattern(ValidatorHelper.urlValidator)),
    LanguageCode: this.fb.control('', Validators.required),
    Comment: this.fb.control(''),
    Company: this.fb.control('', Validators.pattern(ValidatorHelper.companyValidator)),
    JobTitle: this.fb.control('', [Validators.pattern(ValidatorHelper.positionValidator)]),
    VatNumber: this.fb.control('', Validators.pattern(ValidatorHelper.vatNumberValidator)),
    KKZ: this.fb.control('')
  });

  contactName = '';
  leadId: number;
  title: string;
  darkIconUrl = ActionIconHelper.getActionIconUrl('DUPLICATE_CHECK', true);
  contactToDisplay: IContact;
  leadData: ILeadData;
  existingContact: IContact;
  importedContact: IContact;
  differenceKeys: string[];
  duplicates: string[];
  isFinishedLoad = false;
  submittedDuplicate: string;
  exContactCount = 0;
  imContactCount = 0;
  exAddressCount = 0;
  imAddressCount = 0;
  fromDetailDuplicate: boolean;
  selectedDuplicate = 0;
  conflictContacts: IContact[] = [];

  constructor(
    public dialogRef: MatDialogRef<DuplicateCheckComponent>,
    private dialog: MatDialog,
    private readonly router: Router,
    private fb: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public dialogData: { leadId: number, fromDetailDuplicate: boolean },
    @Inject(ACTION_DATA_SERVICE) private actionDataService: ActionDataService, // unused yet
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    private store: Store
  ) {
    super(dialogRef);
    this.leadId = dialogData?.leadId;
    this.fromDetailDuplicate = !!dialogData?.fromDetailDuplicate;
    this.getDuplicateLeadById();
  }

  getDuplicateLeadById(): void {

    this.store.dispatch(new ShowLoader(1000));
    this.leadsDataService.getDuplicateLeadById(this.leadId).subscribe(({success, data, error}) => {
      if (success) {

        if (!!data?.PrimaryConflict) {
          this.conflictContacts = [data?.PrimaryConflict];
        }

        if (data.SecondaryConflicts.length !== 0) {
          for (const secondary of data.SecondaryConflicts) {
            this.conflictContacts.push(secondary);
          }
        }

        this.importedContact = data.ImportedContact;
        this.handleLeadData(data.Lead);

        this.checkDuplicateField();
        this.store.dispatch(new HideLoader());
        this.isFinishedLoad = true;
      } else {
        this.store.dispatch(new HideLoader());

        if (error?.startsWith('404')) {
          this.closeDialog();
          this.router.navigate(['/details/' + this.leadId]);
        }
      }
    });
  }

  handleLeadData(lead: LeadDataObject): void {
    this.contactName = `${lead.Contact.FirstName} ${lead.Contact.LastName}`

    this.leadData = {
      creator: {Id: lead.CreatorId, FirstName: lead.CreatorName.split(' ')[0], LastName: lead.CreatorName.split(' ')[1]},
      creTS: lead.CreTS
    };
  }

  onSelectedContact(count: number): void {
    this.selectedDuplicate = count;
    this.checkDuplicateField();
  }

  checkDuplicateField(): void {
    this.existingContact = this.conflictContacts[this.selectedDuplicate];
    const existingContactKeys = Object.keys(this.existingContact);
    const importedContactKeys = Object.keys(this.importedContact);

    this.duplicates = existingContactKeys.filter((element) => {
        if (element === 'Phone') {
          return this.checkPhoneFields(element);
        }
        return this.existingContact[element] === this.importedContact[element];
      }
    );

    const differences = [...existingContactKeys, ...importedContactKeys];

    this.differenceKeys = differences.filter((element, index, self) =>
      index === self.findIndex((el) =>
        (el === element && !this.duplicates.includes(element))
      )
    );

    this.setContactCount();
    this.setAddressCount();
  }

  checkPhoneFields(element: string): boolean {
    const existingPhone = `${this.existingContact.PhonePrefix} ${this.existingContact[element]}`;
    const importedPhone = `${this.importedContact.PhonePrefix} ${this.importedContact[element]}`;

    const replacedExisting = existingPhone.replace(/^\D+/g, '');
    const replacedImported = importedPhone.replace(/^\D+/g, '');

    return replacedExisting === replacedImported;
  }

  setContactCount(): void {
    const isContact = true;
    this.exContactCount = this.getEmptyCount(Object.keys(this.existingContact), isContact);
    this.imContactCount = this.getEmptyCount(Object.keys(this.importedContact), isContact);

    this.giveAppropriateCount(this.exContactCount, this.imContactCount, isContact);
  }

  setAddressCount(): void {
    const isContact = false;
    this.exAddressCount = this.getEmptyCount(Object.keys(this.existingContact), isContact);
    this.imAddressCount = this.getEmptyCount(Object.keys(this.importedContact), isContact);

    this.giveAppropriateCount(this.exAddressCount, this.imAddressCount, isContact);
  }

  giveAppropriateCount(left: number, right: number, isContact: boolean): void {
    if (left > right) {
      right = left - right;
      left = 0;
    } else if (left === right) {
      left = 0;
      right = 0;
    } else {
      left = right - left;
      right = 0;
    }

    isContact ? this.exContactCount = left : this.exAddressCount = left;
    isContact ? this.imContactCount = right : this.imAddressCount = right;
  }

  getEmptyCount(contact: string[], isContact: boolean): number {
    const fields = isContact ? ['AdditionalEmail', 'Mobile', 'Phone'] : ['Company', 'Address', 'AddressExt', 'City', 'CountryText', 'VatNumber', 'Website'];

    const emptyFields = contact.filter((element, index, self) =>
      element === fields.find((el) =>
        (el === element && !this.duplicates.includes(element))
      )
    );

    return emptyFields.length;
  }

  closeDialog(): void {
    this.clearClassAndCloseDialog(this.dialogRef);
  }

  existingSubmit(): void {
    this.contactForm.patchValue(this.existingContact);
    const contact = this.contactForm.getRawValue();

    const body = {
      Mode: 2,
      NewContact: {...contact},
      ContactsToReplace: [0],
      ExistingContact: contact.Id,
    };

    this.submittedDuplicate = 'existing';
    this.solveDuplicates(body as any);
  }

  importedSubmit(): void {
    this.contactForm.patchValue(this.importedContact);
    const contact = this.contactForm.getRawValue();
    contact['SalutionId'] = this.contactForm.controls.Salution;

    const body = {
      Mode: 1,
      NewContact: {...contact},
      ContactsToReplace: [Number(this.existingContact.Id)],
      ExistingContact: 0
    };

    this.submittedDuplicate = 'imported';
    this.solveDuplicates(body as any);
  }

  solveDuplicates(body: SolveDuplicateContact): void {
    this.store.dispatch(new ShowLoader(1000));
    this.leadsDataService.solveDuplicates(this.leadId, body).subscribe(({success, data, error}) => {
      if (success) {
        this.clearClassAndCloseDialog(this.dialogRef, this.fromDetailDuplicate ? this.submittedDuplicate : 'solved').then(() => {
          this.store.dispatch(new HideLoader());
        });
      } else if (error) {
        console.log(error);
        this.store.dispatch(new HideLoader());
      }
    });
  }

  newMailSubmit(): void {
    const actionData = this.getData();
    const panelClass = 'NEW-EMAIL';

    this.dialog.open(NewEmailComponent, {
      disableClose: true,
      closeOnNavigation: false,
      maxWidth: '100%',
      width: '100%',
      height: '100%',
      maxHeight: '100%',
      panelClass,
      data: actionData || {},
      autoFocus: false
    })?.afterClosed().subscribe(value => {
      if (value === 'updated-mail') {
        this.clearClassAndCloseDialog(this.dialogRef, this.fromDetailDuplicate ? 'update-mail' : 'solved');
      } else if (value === 'simple-closed') {
        this.clearClassAndCloseDialog(this.dialogRef);
      }
    });
  }

  private getData(): any {
    return {
      title: this.title,
      contactName: this.contactName,
      leadId: this.leadId,
      contactForm: this.contactForm,
      importedContact: this.importedContact,
      existingId: this.existingContact.Id
    };
  }

}
